import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import BlockedMitraManager from '../components/BlockedMitraManager'

export default function UserDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [fadeState, setFadeState] = useState('fade-in') // 'fade-in' | 'fade-out'

  // Ambil data user dari Firestore
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const ref = doc(db, 'users', id)
        const snap = await getDoc(ref)
        if (snap.exists()) {
          setUser({ id: snap.id, ...snap.data() })
        } else {
          console.error('❌ User tidak ditemukan.')
        }
      } catch (error) {
        console.error('❌ Gagal mengambil data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchUser()
  }, [id])

  // Tombol kembali dengan animasi fade-out
  const handleBack = () => {
    setFadeState('fade-out')
    setTimeout(() => navigate(-1), 300) // jeda 0.3 detik sebelum pindah
  }

  // Toggle blokir pengguna
  const toggleUserBlock = async () => {
    if (!user) return
    try {
      setSaving(true)
      const newStatus = user.status === 'aktif' ? 'blokir' : 'aktif'
      await updateDoc(doc(db, 'users', user.id), { status: newStatus })
      setUser({ ...user, status: newStatus })
    } catch (error) {
      console.error('❌ Gagal ubah status:', error)
    } finally {
      setSaving(false)
    }
  }

  // Toggle blokir ID HP
  const toggleDeviceBlock = async () => {
    if (!user) return
    try {
      setSaving(true)
      const newDeviceBlock = !user.blockDevice
      await updateDoc(doc(db, 'users', user.id), {
        blockDevice: newDeviceBlock,
      })
      setUser({ ...user, blockDevice: newDeviceBlock })
    } catch (error) {
      console.error('❌ Gagal ubah status ID HP:', error)
    } finally {
      setSaving(false)
    }
  }

  // Loader dark screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <div className="text-gray-400 text-lg animate-pulse">
          Memuat data pengguna...
        </div>
      </div>
    )
  }

  // Jika user tidak ditemukan
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center text-gray-400">
        Data pengguna tidak ditemukan.
      </div>
    )
  }

  // Wrapper dengan animasi dua arah
  return (
    <div
      className={`min-h-screen bg-gray-950 text-gray-200 p-6 transform transition-all duration-500 ${
        fadeState === 'fade-in'
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-2'
      }`}
      style={{ transitionProperty: 'opacity, transform' }}
    >
      {/* Tombol kembali */}
      <button
        onClick={handleBack}
        className="mb-6 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition"
      >
        ← Kembali
      </button>

      {/* Header */}
      <h1 className="text-2xl font-bold mb-2">👤 Detail Pengguna</h1>
      <p className="text-gray-400 mb-6">{user.name}</p>

      {/* Data Pengguna */}
      <div className="bg-gray-900 border border-gray-800 p-6 rounded-xl shadow-lg space-y-3">
        <div>
          <p className="text-sm text-gray-400">Email</p>
          <p className="text-base">{user.email}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Nomor Telepon</p>
          <p className="text-base">{user.phone}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Tanggal Bergabung</p>
          <p className="text-base">{user.joinDate}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">ID HP</p>
          <p className="text-base">{user.deviceId}</p>
        </div>
        <div>
          <p className="text-sm text-gray-400">Status Pengguna</p>
          <span
            className={`px-3 py-1 inline-block rounded-md text-sm font-semibold ${
              user.status === 'aktif'
                ? 'bg-green-700 text-white'
                : 'bg-red-700 text-white'
            }`}
          >
            {user.status}
          </span>
        </div>
        <div>
          <p className="text-sm text-gray-400">Status ID HP</p>
          <span
            className={`px-3 py-1 inline-block rounded-md text-sm font-semibold ${
              user.blockDevice ? 'bg-red-700' : 'bg-green-700'
            } text-white`}
          >
            {user.blockDevice ? 'Diblokir' : 'Aktif'}
          </span>
        </div>
      </div>

      {/* 👈 [BARU] Komponen Daftar Mitra Diblokir */}
      <BlockedMitraManager userId={user.id} />

      {/* Tombol aksi */}
      <div className="flex flex-wrap gap-4 mt-6">
        <button
          onClick={toggleUserBlock}
          disabled={saving}
          className={`px-5 py-2 rounded-lg font-semibold transition ${
            user.status === 'aktif'
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-green-600 hover:bg-green-700'
          } text-white`}
        >
          {saving
            ? 'Menyimpan...'
            : user.status === 'aktif'
            ? 'Blokir Pengguna'
            : 'Aktifkan Pengguna'}
        </button>

        <button
          onClick={toggleDeviceBlock}
          disabled={saving}
          className={`px-5 py-2 rounded-lg font-semibold transition ${
            user.blockDevice
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
          } text-white`}
        >
          {saving
            ? 'Menyimpan...'
            : user.blockDevice
            ? 'Aktifkan ID HP'
            : 'Blokir ID HP'}
        </button>
      </div>
    </div>
  )
}
